import { create } from 'zustand';

/**
 * Store quản lý dữ liệu tuyển dụng
 * Bao gồm: danh sách vị trí tuyển dụng, phân trang, bộ lọc, trạng thái loading
 */
export const useRecruitmentStore = create((set, get) => ({
    // State - Trạng thái dữ liệu
    recruitments: [],       // Danh sách tuyển dụng hiện tại
    groupedRecruitments: [], // Danh sách tuyển dụng đã group theo department
    pageIndex: 1,          // Trang hiện tại
    hasMore: true,          // Còn dữ liệu để load thêm không
    totalItems: 0,          // Tổng số vị trí tuyển dụng
    isLoading: false,       // Trạng thái loading
    lastLoadTime: null,     // Thời gian load dữ liệu cuối cùng

    // Bộ lọc tìm kiếm nâng cao
    filters: {
        keyword: '',           // Từ khóa tìm kiếm (tên vị trí, mô tả)
        departmentId: '',       // ID phòng ban
        businessId: '',         // ID cơ sở/bệnh viện
        employmentType: '',     // Loại hình công việc (1: Full-time, 2: Part-time, 3: Internship, 4: Contract)
        locale: ''              // Ngôn ngữ hiện tại
    },

    // Actions - Các hành động cập nhật state

    // Cập nhật bộ lọc
    setFilters: (patch) => set(state => ({
        filters: { ...state.filters, ...patch }
    })),

    // Thiết lập danh sách tuyển dụng mới (thay thế hoàn toàn)
    setRecruitments: (items, total, page, pageSize = 50) => {
        const grouped = get().groupByDepartment(items);
        set(() => ({
            recruitments: items || [],                          // Danh sách tuyển dụng mới
            groupedRecruitments: grouped,                       // Danh sách đã group
            totalItems: total || 0,                             // Tổng số items
            pageIndex: page || 1,                               // Trang hiện tại
            hasMore: (page || 1) * pageSize < (total || 0),    // Còn dữ liệu để load thêm
            isLoading: false,                                   // Tắt loading
            lastLoadTime: Date.now()                            // Cập nhật thời gian load
        }));
    },

    // Thêm tuyển dụng vào danh sách hiện tại (load more)
    appendRecruitments: (items, total, page, pageSize = 50) => {
        const state = get();
        const allItems = [...state.recruitments, ...(items || [])];
        const grouped = state.groupByDepartment(allItems);
        
        set(() => ({
            recruitments: allItems,                             // Nối thêm vào danh sách cũ
            groupedRecruitments: grouped,                       // Re-group toàn bộ
            totalItems: total || state.totalItems,              // Cập nhật tổng số
            pageIndex: page || state.pageIndex,                 // Cập nhật trang hiện tại
            hasMore: (page || state.pageIndex) * pageSize < (total || state.totalItems), // Kiểm tra còn dữ liệu
            isLoading: false,                                   // Tắt loading
            lastLoadTime: Date.now()                            // Cập nhật thời gian load
        }));
    },

    // Group recruitment theo department
    groupByDepartment: (items) => {
        const grouped = {};
        (items || []).forEach(item => {
            const departmentName = item.department_name || 'Khác';
            const departmentId = item.department_id || 'other';
            const key = `${departmentId}_${departmentName}`;
            
            if (!grouped[key]) {
                grouped[key] = {
                    department_id: departmentId,
                    department_name: departmentName,
                    jobs: []
                };
            }
            grouped[key].jobs.push(item);
        });
        
        return Object.keys(grouped).map(key => ({
            category: grouped[key].department_name,
            department_id: grouped[key].department_id,
            jobs: grouped[key].jobs
        }));
    },

    // Cập nhật trạng thái loading
    setLoading: (loading) => set({ isLoading: loading }),

    // Reset bộ lọc về trạng thái ban đầu
    resetFilters: () => set(state => ({
        filters: {
            keyword: '',                    // Xóa từ khóa
            departmentId: '',               // Xóa phòng ban
            businessId: '',                 // Xóa cơ sở
            employmentType: '',             // Xóa loại hình công việc
            locale: state.filters.locale    // Giữ nguyên ngôn ngữ
        }
    })),

    // Kiểm tra có bộ lọc nào đang active không
    hasActiveFilters: () => {
        const state = get();
        return state.filters.keyword ||           // Có từ khóa tìm kiếm
            state.filters.departmentId ||         // Có chọn phòng ban
            state.filters.businessId ||           // Có chọn cơ sở
            state.filters.employmentType;         // Có chọn loại hình công việc
    },

    // Tạo options cho select dropdown từ danh sách tuyển dụng
    getRecruitmentOptions: () => {
        const state = get();
        return state.recruitments.map(item => ({
            value: item.recruitment_id,
            label: item.recruitment_name,
            // Thêm thông tin phụ nếu cần
            department: item.department_name,
            business: item.business_name,
        }));
    },

    // Tạo options nhóm theo department
    getGroupedRecruitmentOptions: () => {
        const state = get();
        return state.groupedRecruitments.map(group => ({
            label: group.category, // Tên department
            options: group.jobs.map(job => ({
                value: job.recruitment_id,
                label: job.recruitment_name,
                department: job.department_name,
                business: job.business_name,
            }))
        }));
    },

    // Reset toàn bộ store về trạng thái ban đầu
    resetStore: () => set(() => ({
        recruitments: [],
        groupedRecruitments: [],
        pageIndex: 1,
        hasMore: true,
        totalItems: 0,
        isLoading: false,
        lastLoadTime: null,
        filters: {
            keyword: '',
            departmentId: '',
            businessId: '',
            employmentType: '',
            locale: ''
        }
    }))
}));

